import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { base_url } from '../url_variables';
import axios from 'axios';
import { useCookies } from "react-cookie";
import { ClockLoader, GridLoader } from "react-spinners";
import { MenuContext } from "../context/MenuContext";
import { TbPigMoney } from "react-icons/tb";
import { BiMoneyWithdraw } from "react-icons/bi";
import { TopBar } from "../components/topbar";
import { IoIosSearch } from "react-icons/io";
import { FaPlaneArrival, FaPlaneDeparture, FaPlaneSlash } from "react-icons/fa";
import { MdAirplanemodeActive } from "react-icons/md";
import { TostMessageContext } from "../context/TostMessage";
import { BellIcon, PinIcon, CloudOffIcon, AirportField } from "../components/icons";

const Chip = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition",
      active
        ? "bg-[#d8b05a]/20 text-[#d8b05a] border border-[#d8b05a]/25"
        : "text-white/50 hover:text-white/70",
    ].join(" ")}
  >
    {children}
  </button>
);

const Field = ({ label, icon, placeholder }) => (
  <div className="min-w-0">
    <div className="mb-2 text-[11px] tracking-[0.22em] text-white/50">
      {label}
    </div>
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#2E281F] px-4 py-3">
      <Icon name={icon} className="text-white/50" />
      <input
        className="w-full min-w-0 bg-transparent text-sm text-white/70 placeholder:text-white/25 outline-none"
        placeholder={placeholder}
      />
    </div>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded-2xl border border-[#40392B] bg-[#2E281F] px-5 py-4">
    <div className="text-[11px] tracking-[0.22em] text-white/30">{label}</div>
    <div className="mt-2 text-2xl font-semibold text-white/80">{value}</div>
  </div>
);

const Icon = ({ name, className = "" }) => {
  const base = "h-5 w-5";
  switch (name) {
    case "search":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M16.5 16.5 21 21"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "plane-depart":
      return (
        <FaPlaneDeparture className={`${base} ${className}`} />
      );
    case "plane-arrive":
      return (
        <FaPlaneArrival className={`${base} ${className}`} />
      );
    case "swap":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M7 7h12l-2.5-2.5M17 17H5l2.5 2.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "calendar":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M7 3v3M17 3v3"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M4.5 8.5h15"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M6.5 5.5h11A2 2 0 0 1 19.5 7.5v12A2 2 0 0 1 17.5 21.5h-11A2 2 0 0 1 4.5 19.5v-12A2 2 0 0 1 6.5 5.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
        </svg>
      );
    case "tag":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M3.5 12.5V6.8A3.3 3.3 0 0 1 6.8 3.5h5.7l8 8-8.5 8.5-8.5-7.5Z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path
            d="M8.2 8.2h.01"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      );
    case "spark":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M4 16l6-6 4 4 6-8"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M19 6h3v3"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case "star":
      return (
        <svg viewBox="0 0 24 24" className={`${base} ${className}`} fill="none">
          <path
            d="M12 3.5 14.7 9l6 .6-4.6 3.9 1.4 5.9L12 16.9 6.5 19.4l1.4-5.9L3.3 9.6l6-.6L12 3.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const Segment = ({ depTime, depCode, duration, arrTime, arrCode }) => (
  <div className="flex w-full sm:w-auto sm:max-w-[60%] flex-1 items-center gap-4">
    {/* Departure */}
    <div className="px-2">
      <div className="text-xl font-semibold text-white/90">{depTime}</div>
      <div className="mt-0.5 text-xs tracking-widest text-white/50">
        {depCode}
      </div>
    </div>

    {/* Line */}
    <div className="flex flex-1 items-center gap-1">
      <div className="hidden sm:block h-px flex-1 bg-white/10" />
      <div className="flex flex-col items-center">
        <div className="text-xs text-white/40">{duration}</div>
        <div className="mt-1 text-[#d8b05a]/85">
          <MdAirplanemodeActive className="h-4 w-4 text-white" />
        </div>
      </div>
      <div className="hidden sm:block h-px flex-1 bg-white/10" />
    </div>

    {/* Arrival */}
    <div className="px-2 text-right">
      <div className="text-xl font-semibold text-white/90">{arrTime}</div>
      <div className="mt-0.5 text-xs tracking-widest text-white/50">
        {arrCode}
      </div>
    </div>
  </div>
);

export default function FlightSearch() {
    const {state} = useLocation();
    const [tripType, setTripType] = useState("single");
    const [cookies] = useCookies();
    const [loading, setLoading] = useState(false);
    const progress = 0.75; // 75%
    const navigate = useNavigate();
    const {setTostMessage, userProfile} = useContext(TostMessageContext);
    const [pageData, setPageData] = useState({});
    const [airports, setAirports] = useState(cookies?.userProfile?.airports)
    const [fromAirport, setFromAirport] = useState(state?.search?.fromAirport ?? null);
    const [toAirport, setToAirport] = useState(state?.search?.toAirport ?? null);
    const [date, setDate] = useState(state?.search?.date ?? '');

    const canSearch = !!fromAirport && !!toAirport;

    const onFindFlights = () => {
        if (!canSearch) {
            setTostMessage({
                messageType: 'error',
                message: 'Select a departure & arrival airport'
            })
            return;
        };

        // Use these for your API call
        const payload = {
            from: fromAirport?.iata,
            to: toAirport?.iata,
            date,
            fromAirport,
            toAirport,
        };

        getData();
    };

    const miles = useMemo(
        () =>
        new Intl.NumberFormat("en-US", {
            maximumFractionDigits: 0,
        }).format(124500),
        []
    );

    useEffect(() => {
        if (canSearch) getData();
    }, [])

    const getData = async () => {
        setLoading(true);
        const postData = new FormData();
        postData.append('fromAirport', fromAirport?.airport_id);
        postData.append('toAirport', toAirport?.airport_id);
        postData.append('date', date);

        try {
            const response = await axios.post(base_url + '/flight-search/get_data.php', postData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    token: cookies?.userProfile?.session,
                },
            });
            if (response.data.status) {
                setLoading(false);
                setPageData({...pageData, quick_deals: response.data.data, flexible_flights: response.data.flexible_flights })
            } else {
                setTostMessage({
                    messageType: 'error',
                    message: response.data.message
                })
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            setTostMessage({
                messageType: 'error',
                message: 'Network Error'
            })
        }
    }

    return(
        <div className="w-full h-full">
            <TopBar />
            {loading && (
                <div className="flex items-center justify-center h-full w-[-webkit-fill-available] z-[99] fixed">
                    <ClockLoader
                        color={'#d8b05a'}
                        loading={true}
                        size={40}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            <div className="w-full p-4 sm:p-6">
                {/* ===== Quick Flight Search ===== */}
                <div className="relative overflow-hidden rounded-xl border border-[#40392B] bg-[#2E281F]/40 p-5 sm:p-7">
                    <div className="relative">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                        <div className="text-[#d8b05a]">
                            <IoIosSearch size={20} color="#C59F59" />
                        </div>
                        <div className="text-lg font-semibold text-white/90">
                            Find a Flight
                        </div>
                        <div className="hidden sm:block h-px w-48 bg-white/10 ml-2" />
                        </div>

                        <div className="flex items-center gap-2 self-start sm:self-auto">
                        <Chip active={tripType === "single"}>
                            Shared Flight
                        </Chip>
                        <Chip active={tripType === "charter"} onClick={() => navigate('/dashboard/charter-jet')}>
                            Book Full Jet
                        </Chip>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_1fr_220px] lg:items-end">
                        {/* <Field label="FROM" icon="plane-depart" placeholder="Lagos, LOS" />
                        <Field label="TO" icon="plane-arrive" placeholder="London, LHR" /> */}

                        <AirportField
                            label="FROM"
                            icon="plane-depart"
                            airports={airports}
                            value={fromAirport}
                            onChange={setFromAirport}
                            placeholder="Search city, airport, or IATA (e.g. Lagos, LOS)"
                            Icon={Icon}
                        />

                        <AirportField
                            label="TO"
                            icon="plane-arrive"
                            airports={airports}
                            value={toAirport}
                            onChange={setToAirport}
                            placeholder="Search city, airport, or IATA (e.g. London, LHR)"
                            Icon={Icon}
                        />


                        {/* <Field value={date} onChange={(e) => setDate(e.target.value)} label="DATE" icon="calendar" placeholder="Oct 24, 2024" /> */}
                        
                        <div className="min-w-0">
                            <div className="mb-2 text-[11px] tracking-[0.22em] text-white/50">DATE (Leave empty if flexible)</div>
                            <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#2E281F] px-4 py-3">
                            {Icon ? <Icon name="calendar" className="text-white/50" /> : null}
                            <input
                                type="date"
                                value={date}
                                min={new Date().toISOString().split("T")[0]}
                                onChange={(e) => setDate(e.target.value)}
                                className="w-full min-w-0 bg-transparent text-sm text-white/70 outline-none"
                            />
                            </div>
                        </div>

                        <button
                        onClick={onFindFlights}
                        className={[
                            "h-[50px] w-full rounded-xl font-semibold",
                            "bg-[#d8b05a] text-black",
                            "hover:brightness-110 active:brightness-95",
                            "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/20",
                            "transition",
                            `${!canSearch && 'bg-[#d8b05a]/50'}`
                        ].join(" ")}
                        >
                        Find Flights
                        </button>
                    </div>
                    </div>
                </div>

                {/* ===== Lower grid ===== */}
                <div className="mt-7">
                    {/* LEFT: Next Upcoming Flight */}
                    <div>
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                            <div className="text-[#d8b05a]">
                                <Icon name="tag" />
                            </div>
                            {canSearch ? 
                            <div className="text-xl font-semibold text-white/90">
                                Showing flights {fromAirport?.iata != null && `from ${fromAirport?.iata}`} {toAirport?.iata != null && `to ${toAirport?.iata}`} {date != "" && `on ${date}`}
                            </div>
                            : 
                            <div className="text-xl font-semibold text-white/90">
                                Select your departure and arrival city above
                            </div>
                            }
                            </div>
                        </div>
                        <div className="w-full flex flex-col">
                            {pageData?.quick_deals?.map((item, index) => 
                                <div className="mb-2 gap-y-3 sm:gap-y-0 gap-x-3 p-5 border border-[#40392B] bg-[#2E281F]/30 w-full rounded-xl flex flex-col sm:flex-row justify-between items-center sm:items-center">
                                    <div className="flex flex-col items-center gap-2 sm:gap-4">
                                        <div className="flex items-center gap-x-2 truncate text-sm font-semibold text-white">
                                            <Icon name="plane-depart" className="text-white" /> {item?.aircraft_name}
                                        </div>
                                        <div className="truncate text-xs font-regular text-white/80">
                                            {item?.departure_date}
                                        </div>
                                    </div>
                                    <Segment
                                        depTime={item?.departure_time}
                                        depCode={item?.departure_airport_city}
                                        duration={item?.duration}
                                        arrTime={item?.arrival_time}
                                        arrCode={item?.arrival_airport_city}
                                    />
                                    <div className="hidden lg:block h-16 w-px bg-white/10" />
                                    <div className="flex flex-row-reverse sm:flex-col lg:flex-row items-end justify-between gap-4 lg:flex-col lg:items-center lg:justify-center">
                                        <div className="text-right lg:text-center">
                                            <div className="text-[10px] tracking-[0.28em] text-white/40 uppercase">
                                            {item?.flight_fares[0]?.fare_name} FROM
                                            </div>
                                            <div className="mt-1 flex items-center justify-end gap-2 text-2xl font-semibold text-[#d8b05a] lg:justify-center">
                                            {item?.flight_fares[0]?.price}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => navigate('/dashboard/flight-search/confirm-booking', {state: { fromAirport: item?.from_airport, toAirport: item?.to_airport, date: item?.flight_date, flight_no: item?.flight_no, flight_id: item?.flight_id, flight_fares: item?.flight_fares, flight: item }})}
                                            className={[
                                            "rounded-xl px-7 py-2.5 text-xs font-semibold tracking-widest",
                                            "border border-[#d8b05a]/45 text-[#d8b05a]",
                                            "hover:bg-[#d8b05a]/10 active:bg-[#d8b05a]/15",
                                            "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/15",
                                            "transition",
                                            ].join(" ")}
                                        >
                                            Book Now
                                        </button>
                                        <div className="flex items-center gap-4">
                                            <div className="min-w-0">
                                                <div className="truncate text-xs font-regular text-white/50">
                                                    {item?.seat_capacity}{" seat(s) left"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {(!loading && pageData?.quick_deals == null) && 
                                <div className="flex items-center justify-center p-6">
                                    {/* soft warm glow behind */}
                                    <div className="relative w-full max-w-2xl">
                                        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_70%_at_50%_10%,rgba(216,176,90,0.18),transparent_60%)]" />
                                        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(70%_80%_at_50%_80%,rgba(255,255,255,0.06),transparent_60%)]" />

                                        <div className="text-center">
                                        {/* icon badge */}
                                        <div className="mx-auto grid h-[10em] w-[10em] place-items-center rounded-full border border-[#d8b05a]/10 bg-[#d8b05a]/5 shadow-[0_15px_50px_rgba(216,176,90,0.08)]">
                                            <div className="grid place-items-center">
                                            <div className="">
                                                <FaPlaneDeparture size={40} className="text-[#d8b05a]" />
                                            </div>

                                            <div className="mt-6 flex items-center justify-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/50" />
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/80" />
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/50" />
                                            </div>
                                            </div>
                                        </div>

                                        <h2 className="mt-10 text-3xl sm:text-4xl font-semibold text-white/90 leading-tight">
                                            Find the perfect flights to your destination
                                        </h2>                                  
                                        </div>
                                    </div>
                                </div>
                                }
                            {(pageData?.quick_deals?.length < 1) && 
                                <div className="flex items-center justify-center p-6">
                                    {/* soft warm glow behind */}
                                    <div className="relative w-full max-w-2xl">
                                        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(60%_70%_at_50%_10%,rgba(216,176,90,0.18),transparent_60%)]" />
                                        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(70%_80%_at_50%_80%,rgba(255,255,255,0.06),transparent_60%)]" />

                                        <div className="text-center">
                                        {/* icon badge */}
                                        <div className="mx-auto grid h-[10em] w-[10em] place-items-center rounded-full border border-[#d8b05a]/10 bg-[#d8b05a]/5 shadow-[0_15px_50px_rgba(216,176,90,0.08)]">
                                            <div className="grid place-items-center">
                                            <div className="">
                                                <FaPlaneSlash size={40} className="text-[#d8b05a]" />
                                            </div>

                                            <div className="mt-6 flex items-center justify-center gap-2">
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/50" />
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/80" />
                                                <span className="h-2 w-2 rounded-full bg-[#d8b05a]/50" />
                                            </div>
                                            </div>
                                        </div>

                                        <h2 className="mt-10 text-3xl sm:text-4xl font-semibold text-white/90 leading-tight">
                                            No flights available to {toAirport?.iata} on this day
                                        </h2>

                                        <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base leading-7 text-white/40">
                                            We couldn&apos;t find any scheduled flights matching your criteria.
                                            You can request a bespoke private charter or be notified the moment a
                                            seat becomes available.
                                        </p>

                                        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                                            <button
                                            type="button"
                                            onClick={() => navigate('/dashboard/charter-jet')}
                                            className={[
                                                "w-full sm:w-auto inline-flex items-center justify-center gap-2",
                                                "rounded-xl bg-[#d8b05a] px-6 py-3.5",
                                                "text-sm font-semibold text-black",
                                                "hover:brightness-110 active:brightness-95 transition",
                                                "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/20",
                                                "shadow-[0_10px_30px_rgba(216,176,90,0.18)]",
                                            ].join(" ")}
                                            >
                                            <PinIcon className="h-5 w-5" />
                                            Charter Jet
                                            </button>

                                            <button
                                            type="button"
                                            onClick={() => setTostMessage({ messageType: 'success', message: 'You will be notified as soon a a flight on this route becomes available' })}
                                            className={[
                                                "w-full sm:w-auto inline-flex items-center justify-center gap-2",
                                                "rounded-xl border border-[#d8b05a]/35 bg-white/5 px-6 py-3.5",
                                                "text-sm font-semibold text-[#d8b05a]",
                                                "hover:bg-[#d8b05a]/10 transition",
                                                "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/15",
                                            ].join(" ")}
                                            >
                                            <BellIcon className="h-5 w-5" />
                                            Get Notified
                                            </button>
                                        </div>
                                        </div>
                                    </div>
                                    </div>
                            }
                        </div>
                        {(pageData?.flexible_flights?.length > 0 ) && 
                            <>
                            <div className="mb-4 flex flex-col items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="text-[#d8b05a]">
                                        <Icon name="tag" />
                                    </div>
                                    <div className="text-xl font-semibold text-white/90">
                                        <p>Is your schedule flexible?</p>
                                    </div>
                                </div>
                                <p className=" max-w-xl text-sm sm:text-base leading-7 text-white/40">
                                    We found flights to this destination on other days
                                </p>
                            </div>  
                            {pageData?.flexible_flights?.map((item, index) =>
                            <div className="mb-2 gap-x-3 p-5 border border-[#40392B] bg-[#2E281F]/30 w-full rounded-xl flex flex-row justify-between items-center">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="flex items-center gap-x-2 truncate text-sm font-semibold text-white">
                                        <Icon name="plane-depart" className="text-white" /> {item?.aircraft_name}
                                    </div>
                                    <div className="truncate text-xs font-regular text-white/80">
                                        {item?.departure_date}
                                    </div>
                                </div>
                                <Segment
                                    depTime={item?.departure_time}
                                    depCode={item?.departure_airport_city}
                                    duration={item?.duration}
                                    arrTime={item?.arrival_time}
                                    arrCode={item?.arrival_airport_city}
                                />
                                <div className="hidden lg:block h-16 w-px bg-white/10" />
                                <div className="flex items-center justify-between gap-4 lg:flex-col lg:items-center lg:justify-center">
                                    <div className="text-right lg:text-center">
                                        <div className="text-[10px] tracking-[0.28em] text-white/40 uppercase">
                                        {item?.flight_fares[0]?.fare_name} FROM
                                        </div>
                                        <div className="mt-1 flex items-center justify-end gap-2 text-2xl font-semibold text-[#d8b05a] lg:justify-center">
                                        {item?.flight_fares[0]?.currency}{item?.flight_fares[0]?.price}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => navigate('/dashboard/flight-search/confirm-booking', {state: { fromAirport: item?.from_airport, toAirport: item?.to_airport, date: item?.flight_date, flight_no: item?.flight_no, flight_id: item?.flight_id, flight_fares: item?.flight_fares, flight: item }})}
                                        className={[
                                        "rounded-xl px-7 py-2.5 text-xs font-semibold tracking-widest",
                                        "border border-[#d8b05a]/45 text-[#d8b05a]",
                                        "hover:bg-[#d8b05a]/10 active:bg-[#d8b05a]/15",
                                        "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/15",
                                        "transition",
                                        ].join(" ")}
                                    >
                                        Book Now
                                    </button>
                                    <div className="flex items-center gap-4">
                                        <div className="min-w-0">
                                            <div className="truncate text-xs font-regular text-white/50">
                                                {item?.seat_capacity} Seat(s) left
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            )}
                            </>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
}